import { Form, FormControl, FormField, FormItem, FormMessage, FullCV, Input } from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { ArrayPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormArray } from '@/features/common/hooks/useFormArray';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { CompetencesFormValues, createCompetencesSchema } from '../../schemas/competences.schema';
import FormLayout from './FormLayout';
import NumberedItem from './NumberedItem';

interface Props {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const SkillsForm: React.FC<Props> = ({ onSubmit, initialData, onCancel }) => {
  const { t } = useTranslation();

  const competencesSchema = createCompetencesSchema(t);

  const form = useForm<CompetencesFormValues>({
    resolver: zodResolver(competencesSchema),
    defaultValues: initialData || {
      competences: [''],
    },
  });

  const { fields, items, canAdd, handleAdd, handleRemove } = useFormArray<CompetencesFormValues>({
    form,
    name: 'competences' as ArrayPath<CompetencesFormValues>,
    maxItems: 20,
  });

  const handleSubmit = (data: CompetencesFormValues) => {
    onSubmit(data as FullCV);
  };

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'competences');

  return (
    <FormLayout
      title={t('Forms.Competences.title')}
      onCancel={onCancel}
      onSubmit={form.handleSubmit(handleSubmit)}
      showAddButton={canAdd}
      onAdd={() => handleAdd('')}
      addButtonText={t('Forms.Competences.addCompetence')}
    >
      <Form {...form}>
        <form className="space-y-6 pb-20">
          {fields.map((field, index) => (
            <NumberedItem
              key={field.id}
              index={index}
              showDelete={items.length > 1}
              className="items-center"
              onDelete={() => handleRemove(index)}
            >
              <FormField
                control={form.control}
                name={`competences.${index}`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="h-12 w-full min-w-0"
                        placeholder={t('Forms.Competences.competencePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </NumberedItem>
          ))}
        </form>
      </Form>
    </FormLayout>
  );
};

export default SkillsForm;
